import { useEffect, useRef } from 'react';

import { trackEvent } from '../telemetry/telemetry.ts';
import { addToast } from '../toast/toast.ts';
import { useAppVersion } from './use-app-version.ts';

const NOTIFIED_STORAGE_KEY = 'cv:app-update:notified';
const REMIND_LATER_MS = 1000 * 60 * 60 * 24; // 24h
const DEFAULT_DAYS_BEHIND_THRESHOLD = 5;

export type AppUpdateWatcherProps = {
  /** The version baked into the running bundle (`import.meta.env.VITE_VERSION`). */
  currentVersion: string;
  /** How often to re-fetch `/version.txt`. Defaults to one hour. */
  pollIntervalMs?: number;
  /** Days behind at which the prompt escalates and ignores a recent dismissal. */
  daysBehindThreshold?: number;
};

function readLastNotified(version: string): number | null {
  try {
    const raw = window.localStorage.getItem(NOTIFIED_STORAGE_KEY);

    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as { version?: string; at?: number };

    return parsed.version === version && typeof parsed.at === 'number' ? parsed.at : null;
  }
  catch {
    return null;
  }
}

function markNotified(version: string) {
  try {
    window.localStorage.setItem(
      NOTIFIED_STORAGE_KEY,
      JSON.stringify({ version, at: Date.now() }),
    );
  }
  catch {
    // Ignore storage failures (private mode, quota exceeded, ...).
  }
}

/**
 * Watches for a newer deployed build and surfaces a sticky "Reload" toast.
 * Renders nothing. Mount once, high in the tree, inside the toast + query
 * providers (see each app's `routes/__root.tsx`).
 */
export function AppUpdateWatcher({
  currentVersion,
  pollIntervalMs,
  daysBehindThreshold = DEFAULT_DAYS_BEHIND_THRESHOLD,
}: AppUpdateWatcherProps) {
  const { newVersionFound, latestVersion, daysBehind } = useAppVersion({
    currentVersion,
    pollIntervalMs,
  });
  const notifiedVersionRef = useRef<string | null>(null);

  useEffect(() => {
    if (!newVersionFound || !latestVersion) {
      return;
    }

    // Surface a given version at most once per tab lifetime.
    if (notifiedVersionRef.current === latestVersion) {
      return;
    }

    const isStale = daysBehind !== null && daysBehind >= daysBehindThreshold;
    const lastNotified = readLastNotified(latestVersion);
    const recentlyNotified = lastNotified !== null && Date.now() - lastNotified < REMIND_LATER_MS;

    // Respect a recent nudge for this same version (e.g. another tab), unless
    // the tab is now stale enough to escalate.
    if (recentlyNotified && !isStale) {
      return;
    }

    notifiedVersionRef.current = latestVersion;
    markNotified(latestVersion);

    trackEvent('AppUpdateAvailable', {
      currentVersion,
      latestVersion,
      daysBehind,
      escalated: isStale,
    });

    addToast(
      {
        title: 'A new version is available',
        description: isStale
          ? 'Your version is several days out of date. Reload to get the latest updates.'
          : 'Reload to get the latest updates.',
        variant: isStale ? 'warning' : 'info',
        action: {
          label: 'Reload',
          onPress: () => window.location.reload(),
        },
      },
      // timeout 0 → no auto-dismiss timer (react-stately uses a truthy check),
      // so the prompt stays until the user reloads or closes it.
      { timeout: 0 },
    );
  }, [newVersionFound, latestVersion, daysBehind, daysBehindThreshold, currentVersion]);

  return null;
}
